import { Fragment, useEffect, useMemo, useState } from "react";
import { Card } from "reactstrap";
import { getSettingsData } from "../../services/settings";
import Loading from "../../components/utils/Loading";
import Pagination from "../../components/utils/Pagination";
import ContractHeader from "./ContractHeader";
import { useTranslation } from "react-i18next";
import ContractRow from "./ContractRow";
import ContractFormModal from "./ContractFormModal";
import EmptyState from "../../components/EmptyState";
import StandardTable from "../../ui/components/StandardTable";
import StandardTableFilterDropdown from "../../ui/components/StandardTableFilterDropdown";

const Contract = ({ token, API }) => {
  const { t } = useTranslation();

  const defaultData = {
    contract_number: "",
    name: "",
    collector: "",
    collectorEmail: "",
  };

  const [formData, setFormData] = useState(defaultData);
  const [modalOpen, setModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [allContracts, setAllContracts] = useState([]);
  const [contractList, setContractList] = useState([]);

  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [totalEntry, setTotalEntry] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortByField, setSortByField] = useState("contract_number");
  const [orderSortByField, setOrderSortByField] = useState("1");
  const [keywordFilter, setKeywordFilter] = useState("");
  const [collectorFilter, setCollectorFilter] = useState("");

  const hasContracts = useMemo(() => contractList.length > 0, [contractList]);
  const hasAnyContracts = useMemo(
    () => allContracts.length > 0,
    [allContracts],
  );
  const hasActiveFilters = Boolean(
    keywordFilter?.trim() || collectorFilter?.trim(),
  );
  const showEmptyState = !hasAnyContracts && !hasActiveFilters;

  const collectorOptions = useMemo(() => {
    const names = allContracts
      .map((contract) => String(contract?.collector || "").trim())
      .filter(Boolean);
    return [...new Set(names)]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((name) => ({ value: name, label: name }));
  }, [allContracts]);

  const sortContracts = (list, sortBy = "contract_number", order = "1") => {
    const safeSortBy = sortBy || "contract_number";
    return [...list].sort((a, b) => {
      const rawA = a?.[safeSortBy] ?? "";
      const rawB = b?.[safeSortBy] ?? "";
      const aValue = String(rawA).toLowerCase();
      const bValue = String(rawB).toLowerCase();

      if (aValue < bValue) return order === "1" ? -1 : 1;
      if (aValue > bValue) return order === "1" ? 1 : -1;
      return 0;
    });
  };

  const filterContracts = (list, keyword, collector) => {
    let filtered = list;

    if (collector?.trim()) {
      const selectedCollector = collector.trim().toLowerCase();
      filtered = filtered.filter(
        (contract) =>
          String(contract?.collector || "")
            .trim()
            .toLowerCase() === selectedCollector,
      );
    }

    if (keyword?.trim()) {
      const lowerKeyword = keyword.toLowerCase().trim();
      filtered = filtered.filter((contract) => {
        const contractNumber = String(
          contract?.contract_number || "",
        ).toLowerCase();
        const name = String(contract?.name || "").toLowerCase();
        const contractReference =
          `${contractNumber}${name ? ` (${name})` : ""}`.toLowerCase();

        return (
          contractNumber.includes(lowerKeyword) ||
          contractReference.includes(lowerKeyword)
        );
      });
    }

    return filtered;
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const openCreateModal = () => {
    setFormData(defaultData);
    setModalOpen(true);
  };

  const openEditModal = (contract) => {
    setFormData(contract);
    setModalOpen(true);
  };

  const fetchContractData = async () => {
    try {
      const response = await getSettingsData();
      const list = response.data.contractList || [];
      setAllContracts(list);
    } catch (error) {
      alert(error?.response?.data?.error || "Error fetching contracts");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContractData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processedContracts = useMemo(() => {
    const filtered = filterContracts(
      allContracts,
      keywordFilter,
      collectorFilter,
    );
    return sortContracts(filtered, sortByField, orderSortByField);
  }, [
    allContracts,
    keywordFilter,
    collectorFilter,
    sortByField,
    orderSortByField,
  ]);

  useEffect(() => {
    setTotalEntry(processedContracts.length);
    setCurrentPage(1);
  }, [processedContracts]);

  const paginatedContracts = useMemo(() => {
    const startIndex = (currentPage - 1) * recordPerPage;
    const endIndex = startIndex + recordPerPage;
    return processedContracts.slice(startIndex, endIndex);
  }, [processedContracts, currentPage, recordPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [recordPerPage]);

  useEffect(() => {
    setContractList(paginatedContracts);
  }, [paginatedContracts]);

  if (!token) return null;

  if (isLoading) return <Loading color="#F5F5F5" />;

  return (
    <div className="w-full">
      <Card className="table border-0 shadow-none bg-white rounded-lg">
        <StandardTable
          fluidColCount={showEmptyState ? 1 : 3}
          fixedColWidth={showEmptyState ? 0 : 120}
          containerClassName={
            showEmptyState ? "table-standard-container--list-empty" : undefined
          }
          columnAlignments={
            showEmptyState ? ["left"] : ["left", "left", "left", "right"]
          }
          topBar={
            hasAnyContracts ? (
              <section className="table-standard-top-bar">
                <div className="table-standard-top-bar-inner">
                  <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                    <div className="search-input-wrapper">
                      <i className="fas fa-search search-input-icon" />
                      <input
                        type="text"
                        className="search-input"
                        value={keywordFilter}
                        placeholder={t("settingsContacts.searchPlaceholder")}
                        onChange={(event) =>
                          setKeywordFilter(event.target.value)
                        }
                      />
                    </div>
                    <StandardTableFilterDropdown
                      label={t("settingsContacts.collectorFilterLabel")}
                      value={collectorFilter}
                      options={collectorOptions}
                      allLabel={t("all")}
                      alwaysShowLabelPrefix
                      onChange={setCollectorFilter}
                    />
                  </div>
                  <div className="create-button-wrapper">
                    <button
                      type="button"
                      className="create-button"
                      onClick={openCreateModal}
                    >
                      {t("Create a collection email")}
                    </button>
                  </div>
                </div>
              </section>
            ) : null
          }
          header={
            hasAnyContracts ? (
              <ContractHeader
                onSort={(field, order) => {
                  setSortByField(field);
                  setOrderSortByField(order);
                }}
                sortByField={sortByField}
                orderSortByField={orderSortByField}
              />
            ) : null
          }
          bottom={
            hasAnyContracts ? (
              <div className="table-standard-bottom table-pagination-bottom">
                <Pagination
                  totalEntry={totalEntry}
                  recordPerPage={recordPerPage}
                  setRecordPerPage={setRecordPerPage}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  resultPage={resultPage}
                  setResultPage={setResultPage}
                />
              </div>
            ) : null
          }
        >
          {hasContracts ? (
            contractList.map((contract) => (
              <Fragment key={contract._id}>
                <ContractRow
                  contract={contract}
                  contractList={contractList}
                  setContractList={setContractList}
                  allContracts={allContracts}
                  setAllContracts={setAllContracts}
                  openEditModal={openEditModal}
                  token={token}
                  API={API}
                  onDeleted={fetchContractData}
                />
              </Fragment>
            ))
          ) : hasAnyContracts && hasActiveFilters ? (
            <div className="table-standard-row table-standard-row--empty">
              <div
                className="table-standard-row-cell"
                style={{ gridColumn: "span 3" }}
              >
                {t("No results match your search")}
              </div>
              <div className="table-standard-row-cell table-standard-row-actions" />
            </div>
          ) : (
            <EmptyState
              title={t("emptyState.noContract")}
              description={t("emptyState.hintContract")}
              buttonText={t("Create a collection email")}
              onButtonClick={openCreateModal}
            />
          )}
        </StandardTable>
      </Card>

      <ContractFormModal
        isOpen={modalOpen}
        toggle={toggleModal}
        formData={formData}
        setFormData={setFormData}
        fetchContractData={fetchContractData}
        token={token}
        API={API}
      />
    </div>
  );
};

export default Contract;
